"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { Post, Tag } from "@/app/utils/interface";
import Pagination from "@/components/utilities/Pagination";
import TagFilter from "@/components/utilities/TagFilter";
import { client } from "@/sanity/lib/client";

const POSTS_PER_PAGE = 6;

interface TagWithCount extends Tag {
  postCount: number;
}

async function getPosts(page: number = 1, tag?: string) {
  const start = (page - 1) * POSTS_PER_PAGE;
  const end = start + POSTS_PER_PAGE;

  const baseFilter = `_type == "post" && "blog" in categories[]->slug.current`;
  const tagFilter = tag ? ` && $tag in tags[]->slug.current` : "";
  const filter = `${baseFilter}${tagFilter}`;

  const query = `{
    "posts": *[${filter}] | order(publishedAt desc)[$start...$end] {
      _id,
      title,
      navtitle,
      slug,
      publishedAt,
      excerpt,
      pageDescription,
      cloudinaryImage,
      youtubeVideo,
      "categories": categories[]-> {
        _id,
        title,
        slug
      },
      "tags": tags[]-> {
        _id,
        name,
        slug
      }
    },
    "total": count(*[${filter}])
  }`;

  const params = tag
    ? {
        tag,
        start,
        end,
      }
    : {
        start,
        end,
      };

  try {
    const { posts, total } = await client.fetch(
      query,
      params as Record<string, unknown>
    );
    return { posts, total };
  } catch (error) {
    console.error("Error fetching posts:", error);
    return { posts: [], total: 0 };
  }
}

async function getAllTags() {
  const query = `*[_type == "tag"] {
    _id,
    name,
    slug,
    "postCount": count(*[_type == "post" && "blog" in categories[]->slug.current && references(^._id)])
  } | order(postCount desc)`;

  const tags = await client.fetch(query);
  // Filter out tags with 0 posts
  return tags.filter((tag: TagWithCount) => tag.postCount > 0);
}

export default function BlogList() {
  const searchParams = useSearchParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const currentPage = Number(searchParams.get("page")) || 1;
  const selectedTag = searchParams.get("tag") || undefined;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [{ posts, total }, tags] = await Promise.all([
          getPosts(currentPage, selectedTag),
          getAllTags(),
        ]);
        setPosts(posts);
        setTotal(total);
        setTags(tags);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedTag]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="size-32 animate-spin rounded-full border-y-2 border-indigo-600"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / POSTS_PER_PAGE);

  // Separate featured posts (first 3) from regular posts
  const featuredPosts =
    currentPage === 1 && !selectedTag ? posts.slice(0, 3) : [];
  // Latest section: next 6 posts after featured (posts 4-9)
  const latestPosts =
    currentPage === 1 && !selectedTag ? posts.slice(3, 3 + 6) : [];
  const regularPosts =
    currentPage === 1 && !selectedTag ? posts.slice(9) : posts;

  return (
    <>
      <div className="mb-8">
        <TagFilter tags={tags} selectedTag={selectedTag} />
      </div>

      {/* Featured Section - Only on first page without tag filter */}
      {featuredPosts.length > 0 && (
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* Left Column - Large Featured Post */}
          <div className="flex-shrink-0 lg:max-w-[700px]">
            {featuredPosts[0] && (
              <article className="group flex flex-col">
                <Link
                  href={`/${featuredPosts[0].categories[0]?.slug.current}/${featuredPosts[0].slug.current}`}
                  className="block mb-4"
                >
                  {featuredPosts[0].cloudinaryImage?.secure_url ||
                  featuredPosts[0].youtubeVideo ? (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl">
                      <Image
                        src={
                          featuredPosts[0].cloudinaryImage?.secure_url ||
                          `https://img.youtube.com/vi/${featuredPosts[0].youtubeVideo}/maxresdefault.jpg`
                        }
                        alt={featuredPosts[0].title}
                        width={700}
                        height={390}
                        className="w-full h-full object-cover rounded-2xl transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          if (featuredPosts[0].youtubeVideo) {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${featuredPosts[0].youtubeVideo}/hqdefault.jpg`;
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-8 transition-all duration-300 group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600">
                      <h3 className="text-3xl font-bold text-white text-center leading-tight md:text-4xl">
                        {featuredPosts[0].navtitle || featuredPosts[0].title}
                      </h3>
                    </div>
                  )}
                </Link>
                <div className="flex flex-1 flex-col">
                  <h2 className="mb-4 text-3xl font-bold leading-tight text-gray-900 lg:text-[60px]">
                    <Link
                      href={`/${featuredPosts[0].categories[0]?.slug.current}/${featuredPosts[0].slug.current}`}
                      className="transition-colors hover:text-gray-600 hover:underline"
                    >
                      {featuredPosts[0].title}
                    </Link>
                  </h2>
                  {featuredPosts[0].pageDescription && (
                    <p className="mb-4 text-lg leading-relaxed text-gray-600">
                      {featuredPosts[0].pageDescription}
                    </p>
                  )}
                  {featuredPosts[0].excerpt &&
                    !featuredPosts[0].pageDescription && (
                      <p className="mb-4 text-lg leading-relaxed text-gray-600">
                        {featuredPosts[0].excerpt}
                      </p>
                    )}
                  {featuredPosts[0].tags &&
                    featuredPosts[0].tags.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-2">
                        {featuredPosts[0].tags
                          .slice(0, 3)
                          .map(
                            (tag: {
                              _id: string;
                              name: string;
                              slug: { current: string };
                            }) => (
                              <Link
                                key={tag._id}
                                href={`?tag=${tag.slug.current}`}
                                className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-900 hover:text-white"
                              >
                                {tag.name}
                              </Link>
                            )
                          )}
                      </div>
                    )}
                </div>
              </article>
            )}
          </div>

          {/* Right Column - Two Medium Posts */}
          <div className="flex flex-1 flex-col gap-6 lg:pl-8">
            {featuredPosts.slice(1, 3).map((post: Post) => (
              <article key={post._id} className="group flex flex-col">
                <div className="flex gap-4">
                  <div className="flex flex-1 flex-col min-w-0">
                    <h3 className="mb-2 text-lg font-semibold leading-tight text-gray-900 lg:text-xl">
                      <Link
                        href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                        className="transition-colors hover:text-gray-600 hover:underline line-clamp-2"
                      >
                        {post.title}
                      </Link>
                    </h3>
                    {post.pageDescription && (
                      <p className="mb-2 line-clamp-2 text-sm leading-relaxed text-gray-600">
                        {post.pageDescription}
                      </p>
                    )}
                    {post.excerpt && !post.pageDescription && (
                      <p className="mb-2 line-clamp-2 text-sm leading-relaxed text-gray-600">
                        {post.excerpt}
                      </p>
                    )}
                    {post.tags && post.tags.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-1.5">
                        {post.tags
                          .slice(0, 2)
                          .map(
                            (tag: {
                              _id: string;
                              name: string;
                              slug: { current: string };
                            }) => (
                              <Link
                                key={tag._id}
                                href={`?tag=${tag.slug.current}`}
                                className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                              >
                                {tag.name}
                              </Link>
                            )
                          )}
                      </div>
                    )}
                  </div>
                  <Link
                    href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                    className="relative w-24 h-24 shrink-0 overflow-hidden rounded-2xl"
                  >
                    {post.cloudinaryImage?.secure_url || post.youtubeVideo ? (
                      <Image
                        src={
                          post.cloudinaryImage?.secure_url ||
                          `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`
                        }
                        alt={post.title}
                        fill
                        sizes="96px"
                        className="rounded-2xl object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          if (post.youtubeVideo) {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                          }
                        }}
                      />
                    ) : (
                      <div className="h-full w-full rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-2">
                        <span className="text-xs font-bold text-white text-center leading-tight line-clamp-3">
                          {post.navtitle || post.title}
                        </span>
                      </div>
                    )}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {/* Latest Section - Only on first page without tag filter */}
      {latestPosts.length > 0 && (
        <>
          {/* Full Width Gray Divider */}
          <div className="my-12 h-px w-full bg-gray-300"></div>
          
          {/* Latest Title */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
              Latest
            </h2>
          </div>

          {/* Latest Posts Grid - 6 posts */}
          <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {latestPosts.map((post: Post) => (
              <article
                key={post._id}
                className="group flex flex-col transition-all duration-300"
              >
                <Link
                  href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                  className="block"
                >
                  {post.cloudinaryImage?.secure_url || post.youtubeVideo ? (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gray-100">
                      <Image
                        src={
                          post.cloudinaryImage?.secure_url ||
                          `https://img.youtube.com/vi/${post.youtubeVideo}/maxresdefault.jpg`
                        }
                        alt={post.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                        style={{ borderRadius: "1.5rem" }}
                        onError={(e) => {
                          if (post.youtubeVideo) {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-6 transition-all duration-300 group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600">
                      <h3 className="text-xl font-bold text-white text-center leading-tight md:text-2xl">
                        {post.navtitle || post.title}
                      </h3>
                    </div>
                  )}
                </Link>
                <div className="flex flex-1 flex-col pt-4">
                  <h2 className="mb-3 text-2xl font-semibold leading-tight text-gray-900">
                    <Link
                      href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                      className="transition-colors hover:text-gray-600 hover:underline"
                    >
                      {post.title}
                    </Link>
                  </h2>
                  {post.pageDescription && (
                    <p className="mb-3 text-base leading-relaxed text-gray-600">
                      {post.pageDescription}
                    </p>
                  )}
                  {post.excerpt && (
                    <p className="line-clamp-2 text-base leading-relaxed text-gray-600">
                      {post.excerpt}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {post.tags
                      ?.slice(0, 2)
                      .map(
                        (tag: {
                          _id: string;
                          name: string;
                          slug: { current: string };
                        }) => (
                          <Link
                            key={tag._id}
                            href={`?tag=${tag.slug.current}`}
                            className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                          >
                            {tag.name}
                          </Link>
                        )
                      )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {/* Regular Posts Grid */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {regularPosts.map((post: Post) => (
          <article
            key={post._id}
            className="group flex flex-col transition-all duration-300"
          >
            <Link
              href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
              className="block"
            >
              {post.cloudinaryImage?.secure_url || post.youtubeVideo ? (
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gray-100">
                  <Image
                    src={
                      post.cloudinaryImage?.secure_url ||
                      `https://img.youtube.com/vi/${post.youtubeVideo}/maxresdefault.jpg`
                    }
                    alt={post.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ borderRadius: "1.5rem" }}
                    onError={(e) => {
                      if (post.youtubeVideo) {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-6 transition-all duration-300 group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600">
                  <h3 className="text-xl font-bold text-white text-center leading-tight md:text-2xl">
                    {post.navtitle || post.title}
                  </h3>
                </div>
              )}
            </Link>
            <div className="flex flex-1 flex-col pt-4">
              <h2 className="mb-3 text-2xl font-semibold leading-tight text-gray-900">
                <Link
                  href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                  className="transition-colors hover:text-gray-600 hover:underline"
                >
                  {post.title}
                </Link>
              </h2>
              {post.pageDescription && (
                <p className="mb-3 text-base leading-relaxed text-gray-600">
                  {post.pageDescription}
                </p>
              )}
              {post.excerpt && (
                <p className="line-clamp-2 text-base leading-relaxed text-gray-600">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {post.tags
                  ?.slice(0, 2)
                  .map(
                    (tag: {
                      _id: string;
                      name: string;
                      slug: { current: string };
                    }) => (
                      <Link
                        key={tag._id}
                        href={`?tag=${tag.slug.current}`}
                        className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                      >
                        {tag.name}
                      </Link>
                    )
                  )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-12">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          selectedTag={selectedTag}
        />
      </div>
    </>
  );
}
